import React, { useEffect, useState } from 'react';
import { cn } from '../../../../lib/utils';
import { semiBoldText } from '../../../../styling/stylingHelper';
import { FrontendStaffUser } from '../../../../services/staff/staffService';

interface StaffAvatarProps {
  user: FrontendStaffUser;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'w-10 h-10 text-sm',
  md: 'w-[50px] h-[50px] text-base',
  lg: 'w-14 h-14 text-lg',
};

function getInitials(user: FrontendStaffUser): string {
  const source = (user.name || user.email || '?').trim();
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

/**
 * Profile picture with an initials fallback. Avatar urls are S3 links that can 404
 * or expire, so a load failure falls back to initials rather than a broken image.
 */
const StaffAvatar: React.FC<StaffAvatarProps> = ({ user, size = 'md', className }) => {
  const [failed, setFailed] = useState(false);

  // A recycled list row can hand this component a different user; forget the
  // previous user's failure so their avatar gets a fresh attempt.
  useEffect(() => {
    setFailed(false);
  }, [user.profile_picture_url]);

  const classes = cn('rounded-full object-cover flex-shrink-0', sizeClasses[size], className);

  if (user.profile_picture_url && !failed) {
    return (
      <img
        src={user.profile_picture_url}
        alt={user.name || user.email}
        className={classes}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div
      className={cn(
        classes,
        'flex items-center justify-center',
        semiBoldText,
        'bg-cardLightGreen dark:bg-cardLightGreen-dark text-darkGreenColor dark:text-darkGreenColor-dark'
      )}
      aria-hidden
    >
      {getInitials(user)}
    </div>
  );
};

export default StaffAvatar;
